import { Theme } from '@mui/material'
import { FormFieldType, FunctionType } from 'src/enums'

export enum ReportComponentType {
  TopBar = 1,
  DataTable = 2,
  Text = 3,
  Function = 4,
  Instruction = 5,
}

export enum ColumnSortDirection {
  Asc = 1,
  Desc = 2,
  None = 3,
}

export enum TextType {
  Header = 1,
  Body = 2,
}

export enum TextAlignment {
  Left = 1,
  Center = 2,
  Right = 3,
  Justify = 4,
}

export enum TableSortDirection {
  Asc = 1,
  Desc = 2,
  None = 3,
}

export const textAlignmentToCss = (alignment: TextAlignment | undefined): 'left' | 'center' | 'right' | 'justify' => {
  switch (alignment) {
    case TextAlignment.Center:
      return 'center'
    case TextAlignment.Right:
      return 'right'
    case TextAlignment.Justify:
      return 'justify'
    default:
      return 'left'
  }
}

export interface ITextStyles {
  fontColor: string
  bold: boolean
  alignment: TextAlignment
  fontSize: number
  fontFamily: string
  lineHeight: number
  letterSpacing: number
}

export const DEFAULT_HEADER_STYLES = (theme: Theme): ITextStyles => ({
  fontColor: theme.palette.primaryDark.main,
  bold: true,
  alignment: TextAlignment.Left,
  fontSize: 24,
  fontFamily: 'Arial',
  lineHeight: 1.4,
  letterSpacing: 0,
})

export const DEFAULT_BODY_STYLES = (theme: Theme): ITextStyles => ({
  fontColor: theme.palette.common.black,
  bold: false,
  alignment: TextAlignment.Left,
  fontSize: 14,
  fontFamily: 'Arial',
  lineHeight: 1.4,
  letterSpacing: 0,
})

export const DEFAULT_TOP_BAR_TITLE_STYLES = (theme: Theme): ITextStyles => ({
  fontColor: theme.palette.common.white,
  bold: true,
  alignment: TextAlignment.Left,
  fontSize: 24,
  fontFamily: 'Arial',
  lineHeight: 1.25,
  letterSpacing: 0,
})

export const DEFAULT_TOP_BAR_SUBTITLE_STYLES = (theme: Theme): ITextStyles => ({
  fontColor: theme.palette.grey[400],
  bold: false,
  alignment: TextAlignment.Left,
  fontSize: 14,
  fontFamily: 'Arial',
  lineHeight: 1.4,
  letterSpacing: 0,
})

export const DEFAULT_FUNCTION_STYLES = (theme: Theme): ITextStyles => ({
  fontColor: theme.palette.common.black,
  bold: false,
  alignment: TextAlignment.Left,
  fontSize: 14,
  fontFamily: 'Arial',
  lineHeight: 1.4,
  letterSpacing: 0,
})

export interface ITableColumnConfig {
  id: string
  formSchemaFieldId: string | null
  fieldName: string
  orderNumber: number
  width: number | null
  schemaFieldType: FormFieldType | null
}

export interface IBaseComponentInstance {
  id: string
  type: ReportComponentType
  label: string
}

export interface ITableStyles {
  headerColor: string
  headerBackgroundColor: string
  fontSize: number
  rowHeight: number
}

export const DEFAULT_TABLE_STYLES = (theme: Theme): ITableStyles => ({
  headerColor: theme.palette.primaryDark.main,
  headerBackgroundColor: theme.palette.grey[300],
  fontSize: 14,
  rowHeight: 28,
})

export interface ITableComponentInstance extends IBaseComponentInstance {
  type: ReportComponentType.DataTable
  columns: ITableColumnConfig[]
  tableStyles: ITableStyles
  sortColumnId: string | null
  sortDirection: TableSortDirection
}

export interface ITextComponentInstance extends IBaseComponentInstance {
  type: ReportComponentType.Text
  content: string
  textType: TextType
  styles: ITextStyles
}

export interface IFunctionComponentInstance extends IBaseComponentInstance {
  type: ReportComponentType.Function
  functionType: FunctionType | null
  selectedColumnId: string | null
  selectedColumnName: string | null
  styles: ITextStyles
}

export interface ITopBarComponentInstance extends IBaseComponentInstance {
  type: ReportComponentType.TopBar
  title: string
  subtitle: string
  backgroundColor: string
  titleStyles: ITextStyles
  subtitleStyles: ITextStyles
}

export interface IInstructionComponentInstance extends IBaseComponentInstance {
  type: ReportComponentType.Instruction
  columns: ITableColumnConfig[]
}

export type IReportComponentInstance =
  | ITableComponentInstance
  | ITextComponentInstance
  | IFunctionComponentInstance
  | ITopBarComponentInstance
  | IInstructionComponentInstance

export interface IInstructionLookUpEntry {
  identifier: string
  content: string
  type: FormFieldType
}

export enum GenerateReportDateStatus {
  all,
  custom,
}
